import { ref } from 'vue';

import axios from 'axios';
import { DatabaseEntities, DatabaseOperations } from '@nova/core';

import type { Ref } from 'vue';

export interface Webhook {
  id: string;
  orgId: string;
  entity: DatabaseEntities;
  operation: DatabaseOperations;
  targetUrl: string;
  warehouseId?: string | null;
  body?: Record<string, unknown> | null;
  headers?: Record<string, string> | null;
  isActive: boolean;
  createDateTime: string;
}

export interface CreateWebhookPayload {
  orgId: string;
  entity: DatabaseEntities;
  operation: DatabaseOperations;
  targetUrl: string;
  body?: Record<string, unknown> | null;
  headers?: Record<string, string> | null;
}

export type UpdateWebhookPayload = Partial<
  Pick<Webhook, 'targetUrl' | 'body' | 'headers' | 'isActive'>
>;

/** Outcome of POST /webhook/test. `ok` is true only for a 2xx from the target. */
export interface TestWebhookResult {
  ok: boolean;
  statusCode?: number;
  durationMs: number;
  responseBody?: string;
  error?: string;
  requestBody: Record<string, unknown>;
}

/** What the create/edit form produces. `entity` and `operation` are ignored on edit. */
export interface WebhookFormPayload {
  entity: DatabaseEntities;
  operation: DatabaseOperations;
  targetUrl: string;
  headers: Record<string, string> | null;
  body: Record<string, unknown> | null;
}

/**
 * Internal-only webhook management for a single org, backed by /webhook.
 *
 * Errors are not notified here: the satellite AxiosPlugin response interceptor already
 * surfaces the server's message. Mutations resolve to `true` on success and `false` on
 * failure so the caller can decide what to do next (e.g. keep the form open), and the
 * list is re-fetched either way so a failure never leaves stale rows on screen.
 */
export function useWebhooks(orgId: string) {
  const webhooks: Ref<Webhook[]> = ref([]);
  const loading = ref(false);
  const saving = ref(false);
  const testing = ref(false);
  /** False until the first fetch settles, so the dialog can tell "empty" from "not asked yet". */
  const hasLoaded = ref(false);

  const refresh = async (): Promise<void> => {
    loading.value = true;
    try {
      const response = await axios.get<Webhook[]>('webhook', { params: { orgId } });

      /*
       * The AxiosPlugin interceptor resolves with `undefined` when a request is canceled
       * rather than rejecting, so guard the shape: blanking the list on a canceled refresh
       * would look exactly like "the org has no webhooks".
       */
      if (Array.isArray(response?.data)) {
        webhooks.value = response.data;
        hasLoaded.value = true;
      }
    } finally {
      loading.value = false;
    }
  };

  /*
   * Every mutation re-fetches in `finally`, including on failure. A 409 from create means a
   * matching webhook already exists, and the useful thing to show is the row that is blocking
   * the user - not the list as it was before they tried.
   */
  const mutate = async (request: () => Promise<unknown>): Promise<boolean> => {
    saving.value = true;
    try {
      await request();
      return true;
    } catch (error) {
      /*
       * Logged rather than swallowed silently. A bare `catch { return false }` hid a real bug for
       * a while: a 201 create was rejected by a response interceptor, so the caller saw failure
       * with no trace of why. The user-facing message still comes from the AxiosPlugin
       * interceptor; this is purely a breadcrumb.
       */
      console.error('useWebhooks: mutation failed', error);
      return false;
    } finally {
      await refresh().catch(() => undefined);
      saving.value = false;
    }
  };

  const create = (payload: CreateWebhookPayload): Promise<boolean> =>
    mutate(() => axios.post('webhook', payload));

  const update = (id: string, payload: UpdateWebhookPayload): Promise<boolean> =>
    mutate(() => axios.patch(`webhook/${id}`, payload));

  const remove = (id: string): Promise<boolean> => mutate(() => axios.delete(`webhook/${id}`));

  /*
   * Fires the payload at the target URL without saving it. Neutron sends the request, not the
   * browser: the target is a third-party URL, so a direct call would need a CORS preflight that
   * webhook receivers do not answer, and the response would be unreadable. Going through the
   * API also means the test uses the same egress and payload builder as the real sender.
   *
   * A non-2xx from the target still resolves - it comes back as `ok: false` with the status.
   * Only a failure to reach Neutron itself resolves to null.
   */
  const test = async (payload: CreateWebhookPayload): Promise<TestWebhookResult | null> => {
    testing.value = true;
    try {
      const response = await axios.post<TestWebhookResult>('webhook/test', payload);
      return response?.data ?? null;
    } catch {
      return null;
    } finally {
      testing.value = false;
    }
  };

  return { webhooks, loading, saving, testing, hasLoaded, refresh, create, update, remove, test };
}
